// src/components/copilot/components/StructuredDataForm.jsx
import {
  CheckCircle as CheckIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  Box,
  Button,
  Chip,
  Divider,
  IconButton,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { green, grey, blue } from "@mui/material/colors";
import { memo, useCallback, useState } from "react";

const StructuredDataForm = memo(({ data, schema, className, onSave }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editedData, setEditedData] = useState(data);
  const [isSaved, setIsSaved] = useState(false);

  const handleEditField = useCallback((field, value) => {
    setEditedData((prev) => ({
      ...prev,
      [field]: value,
    }));
  }, []);

  const handleStartEdit = useCallback(() => {
    setIsEditing(true);
    setEditedData(data);
  }, [data]);

  const handleCancelEdit = useCallback(() => {
    setIsEditing(false);
    setEditedData(data);
  }, [data]);

  const handleSave = useCallback(() => {
    onSave({
      data: editedData,
      schema,
      className,
    });
    setIsEditing(false);
    setIsSaved(true);
  }, [editedData, schema, className, onSave]);

  const formatFieldValue = (value, fieldSchema) => {
    if (value === null || value === undefined || value === "") {
      return <i style={{ color: grey[500] }}>Not provided</i>;
    }

    if (fieldSchema?.form_type === "date") {
      return new Date(value).toLocaleDateString();
    }

    return value;
  };

  const renderField = (field, value) => {
    const fieldSchema = schema?.properties?.[field];
    if (!fieldSchema) return null;

    const fieldTitle =
      fieldSchema.title || field.replace(/([A-Z])/g, " $1").trim();
    const fieldType = fieldSchema.form_type || "text";
    const options = fieldSchema.options;
    const description = fieldSchema.description;

    // Skip empty fields in view mode
    if (!isEditing && (value === null || value === "" || value === undefined)) {
      return null;
    }

    return (
      <Box key={field} sx={{ mb: 2 }}>
        <Typography
          variant="caption"
          sx={{
            display: "block",
            mb: 0.5,
            fontWeight: 600,
            color: "text.secondary",
            textTransform: "capitalize",
          }}
        >
          {fieldTitle}
          {description && (
            <Tooltip title={description} placement="top">
              <Box
                component="span"
                sx={{
                  ml: 0.5,
                  cursor: "help",
                  color: "info.main",
                  fontSize: "0.875rem",
                }}
              >
                ⓘ
              </Box>
            </Tooltip>
          )}
        </Typography>

        {isEditing ? (
          <>
            {fieldType === "select" && options ? (
              <TextField
                select
                fullWidth
                size="small"
                value={value || ""}
                onChange={(e) => handleEditField(field, e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    borderRadius: "8px",
                  },
                }}
              >
                <MenuItem value="">
                  <em>Select...</em>
                </MenuItem>
                {options.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            ) : fieldType === "date" ? (
              <TextField
                type="date"
                fullWidth
                size="small"
                value={value ? new Date(value).toISOString().split("T")[0] : ""}
                onChange={(e) => handleEditField(field, e.target.value)}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    borderRadius: "8px",
                  },
                }}
              />
            ) : fieldType === "stringMultiline" ? (
              <TextField
                multiline
                rows={3}
                fullWidth
                size="small"
                value={value || ""}
                onChange={(e) => handleEditField(field, e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    borderRadius: "8px",
                  },
                }}
              />
            ) : (
              <TextField
                fullWidth
                size="small"
                value={value || ""}
                onChange={(e) => handleEditField(field, e.target.value)}
                sx={{
                  "& .MuiOutlinedInput-root": {
                    bgcolor: "white",
                    borderRadius: "8px",
                  },
                }}
              />
            )}
          </>
        ) : (
          <Box
            sx={{
              p: 1.5,
              bgcolor: grey[50],
              borderRadius: "8px",
              border: `1px solid ${grey[200]}`,
            }}
          >
            <Typography variant="body2">
              {formatFieldValue(value, fieldSchema)}
            </Typography>
          </Box>
        )}
      </Box>
    );
  };

  return (
    <Box
      sx={{
        mt: 2,
        pt: 2,
        borderTop: `1px solid ${grey[300]}`,
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          {isSaved ? (
            <CheckIcon sx={{ fontSize: 20, color: green[600] }} />
          ) : (
            <EditIcon sx={{ fontSize: 20, color: "primary.main" }} />
          )}
          <Typography variant="subtitle2" fontWeight={600}>
            {className} Form
          </Typography>
          {isSaved && (
            <Chip
              label="Saved"
              size="small"
              sx={{
                ml: 1,
                height: 20,
                bgcolor: green[50],
                color: green[700],
                border: `1px solid ${green[200]}`,
              }}
            />
          )}
        </Box>

        {!isSaved && !isEditing && (
          <Tooltip title="Edit form data">
            <IconButton size="small" onClick={handleStartEdit}>
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Box>

      {/* Form Fields Container */}
      <Box
        sx={{
          p: 2,
          bgcolor: isEditing ? blue[50] : grey[50],
          borderRadius: "12px",
          border: `1px solid ${isEditing ? blue[200] : grey[200]}`,
        }}
      >
        {Object.entries(editedData).map(([field, value]) =>
          renderField(field, value),
        )}
      </Box>

      {/* Action Buttons */}
      {!isSaved && (
        <Box
          sx={{
            display: "flex",
            gap: 1,
            mt: 2,
            justifyContent: "flex-end",
          }}
        >
          {isEditing ? (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={handleCancelEdit}
                startIcon={<CloseIcon />}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                }}
              >
                Cancel
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                startIcon={<SaveIcon />}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                }}
              >
                Save
              </Button>
            </>
          ) : (
            <>
              <Button
                size="small"
                variant="outlined"
                onClick={handleStartEdit}
                startIcon={<EditIcon />}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                }}
              >
                Edit
              </Button>
              <Button
                size="small"
                variant="contained"
                onClick={handleSave}
                startIcon={<CheckIcon />}
                sx={{
                  borderRadius: "8px",
                  textTransform: "none",
                }}
              >
                Confirm & Save
              </Button>
            </>
          )}
        </Box>
      )}
    </Box>
  );
});

StructuredDataForm.displayName = "StructuredDataForm";

export default StructuredDataForm;
